import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminsApi, type AdminAccount } from '../api/admins.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Spinner, ErrorBox, EmptyBox, Field } from '../components/ui.js';
import { datetime } from '../lib/format.js';
import { MENU, SEC_PREFIX } from '../menu.js';

/**
 * 관리자 계정 등록/수정.
 *
 * 여기서 다루는 계정은 레거시와 공유하는 admin_login 이 아니라 신규 테이블
 * xadmin_account 에만 있다(server/src/modules/admins 참고) - 이 화면을
 * 통해서는 admin_login 을 절대 건드리지 않는다.
 *
 * 아이디(userid)는 수정할 수 없다 - 로그인·created_by 등 여러 곳이 아이디를
 * 키로 쓰고 있어서, 바꿔야 한다면 계정을 새로 등록하는 편이 안전하다
 * (auth.service.ts updateAdminAccount 참고). 고칠 수 있는 건 표시 이름·직책·
 * 등급·활성 여부·비밀번호뿐이다. 직책(position)은 등급(authtype)과 무관한
 * 순수 표시 값이라 권한에는 영향을 주지 않는다 - 목록에 이름 옆 괄호로 보여준다.
 *
 * 등급(authtype)은 두 가지를 조합해서 저장한다.
 *  - 최고 관리자 권한('admin'): 은행입금·이메일다운로드 등 민감 기능을 포함해
 *    전부 열린다. '설정'(관리자 계정·화이트 IP)만은 예외라, 이 권한을 켜도
 *    자동으로 보이지 않는다 - xadmin 한 명만 본다(레거시부터 항상 그래 왔다 -
 *    lib/settingsAccess.ts 참고). '설정'은 아래 섹션 체크박스에도 없다 -
 *    한때 sec:settings 로 계정마다 개별 부여할 수 있었지만, 최고 관리자가
 *    아닌 계정도 다른 관리자 계정·화이트 IP 를 건드릴 수 있게 돼(2026-09-03)
 *    완전히 없앴다. 서버도 /api/admins, /api/whitelist 를 requireRole('admin')
 *    단독으로 되돌렸다 - 이 화면에 오는 방법은 이제 '최고 관리자 권한'뿐이다.
 *  - '설정'을 뺀 메뉴 섹션 13개를 하나씩(`sec:<섹션key>`) - 서로 묶이지 않고
 *    독립적으로 켜고 끌 수 있다. '통계'·'물류센터'(CS 리포트 포함)는 각자 자기
 *    조회 API 를 그 섹션 하나만으로 연다(xadm/index.ts 참고) - 관리자 등급이
 *    아니어도 그 섹션만 선택하면 그대로 볼 수 있다. 나머지 11개는 실제 쓰기
 *    API(상품/카테고리 CRUD, 오프라인 매장 정산, 랜덤박스 주문·배송 등)가
 *    예전처럼 두 묶음(매장 계열/전사 계열) 단위로만 열린다 - 그 API들이 여러
 *    섹션에서 같은 라우터를 공유하고 있어서, 완전히 쪼개려면 돈·재고를 다루는
 *    그 코드들을 전부 다시 설계해야 한다(server/src/modules/reports/authz.ts
 *    의 ALL_SECTION_KEYS 주석 참고). 메뉴 표시와 페이지 이동은 13개 전부
 *    진짜 독립이다.
 *
 * app.ts 가 /api/admins 를 requireRole('admin') 뒤에 두므로, 이 권한이 없는
 * 사람이 이 화면에 와도 목록/등록/수정 요청은 서버에서 403 으로 막힌다.
 */

const MIN_PASSWORD_LENGTH = 12;

/** 최고 관리자 권한 토큰. 은행입금·이메일다운로드 등 민감 기능은 이 값만 본다(server authz.ts ELEVATED_ROLES). */
const ADMIN_TOKEN = 'admin';

/**
 * 이 화면에서 섹션 단위로 켜고 끌 수 있는 섹션 - '설정'은 뺀다. '설정'(관리자
 * 계정·화이트 IP)은 sec:<key> 로 개별 부여하는 방식 자체를 없앴다 - 최고
 * 관리자 권한이 있는 xadmin 한 명만 본다(settingsAccess.ts, HomePage.tsx,
 * Layout.tsx 참고).
 */
const SELECTABLE_SECTIONS = MENU.filter((s) => s.key !== 'settings');

/**
 * 새 계정을 등록할 때 기본으로 켜 둘 섹션 - 통계·서버체크·네트워크 광고 현황만
 * 뺀 나머지 전부. 서버체크(Redis 상황체크)는 통계와 같은 이유로 뺐다 - 대부분의
 * 신규 계정에게 기본으로 줄 만한 화면이 아니라, 필요한 사람에게만 따로 체크해서
 * 준다. 네트워크 광고 현황(2026-09-11)도 같다 - 광고비·매출을 보고 원천 재계산까지
 * 하는 admin 전용 화면이다.
 */
const DEFAULT_SECTION_KEYS = SELECTABLE_SECTIONS.filter(
  (s) => s.key !== 'stats' && s.key !== 'serverCheck' && s.key !== 'network',
).map((s) => s.key);

/**
 * 예전 3단계 등급(offline/product)이 실제로 열어주던 섹션 집합.
 * server/src/modules/reports/authz.ts 의 OFFLINE_TIER_SECTION_KEYS /
 * PRODUCT_TIER_SECTION_KEYS 와 반드시 같아야 한다 - 예전 방식으로 등록된
 * 계정을 편집 화면에 띄울 때만 쓴다(아래 authtypesToFormState 참고).
 */
const LEGACY_OFFLINE_TIER_KEYS = ['product1', 'product2', 'content', 'review', 'offlineStore', 'payment', 'etc'];
const LEGACY_PRODUCT_TIER_KEYS = ['product3', 'marketing', 'logistics', 'lottery', 'randombox', 'siteManagement'];

interface AccessFormState {
  elevatedAdmin: boolean;
  sections: string[];
}

/**
 * 서버가 내려주는 authtypes 문자열 배열을 이 화면의 체크박스 상태로 바꾼다.
 *
 * 이미 새 방식(`sec:<key>`)으로 저장된 계정은 그 값을 그대로 쓴다. 아직 예전
 * 3단계로 저장된 계정을 편집 화면에 열면, 그 등급이 실제로 열어주던 섹션
 * 집합으로 풀어서 보여준다 - 그대로 저장하면 예전 값 대신 새 sec: 토큰
 * 집합으로 바뀌지만, 실제로 열리는 화면 범위는 같다.
 *
 * '설정'은 이제 섹션 체크박스 자체가 없다(SELECTABLE_SECTIONS 참고) - 그래서
 * sec:settings 는 여기서 걸러낸다. 예전에(2026-09-03 이전) sec:settings 로
 * 개별 부여받았던 계정을 이 화면에서 한 번 열었다가 다른 값만 바꾸고 저장해도,
 * 그 값은 다시 저장되지 않고 자연히 빠진다 - 이 필터가 하는 일이다.
 */
function authtypesToFormState(authtypes: string[]): AccessFormState {
  const sectionTokens = authtypes
    .filter((t) => t.startsWith(SEC_PREFIX))
    .map((t) => t.slice(SEC_PREFIX.length))
    .filter((key) => key !== 'settings');
  if (sectionTokens.length > 0 || authtypes.includes(ADMIN_TOKEN)) {
    return { elevatedAdmin: authtypes.includes(ADMIN_TOKEN), sections: sectionTokens };
  }

  const sections = new Set<string>();
  if (authtypes.includes('offline') || authtypes.includes('product')) {
    for (const k of LEGACY_OFFLINE_TIER_KEYS) sections.add(k);
  }
  if (authtypes.includes('product')) {
    for (const k of LEGACY_PRODUCT_TIER_KEYS) sections.add(k);
  }
  return { elevatedAdmin: false, sections: Array.from(sections) };
}

function buildAuthtypes(state: AccessFormState): string[] {
  return [...(state.elevatedAdmin ? [ADMIN_TOKEN] : []), ...state.sections.map((k) => `${SEC_PREFIX}${k}`)];
}

/**
 * 목록 표의 등급 칸. 예전엔 섹션 이름을 쉼표로 이어 한 줄에 늘어놨는데, 섹션을
 * 열 개 넘게 가진 계정이면 그 한 줄이 표를 다 먹어 훑어보기가 어려웠다 -
 * 배지로 끊어 보여주고 많으면 접는다.
 */
const ACCESS_CHIP_LIMIT = 4;

function AccessCell({ authtypes }: { authtypes: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const { elevatedAdmin, sections } = authtypesToFormState(authtypes);
  const titles = MENU.filter((s) => sections.includes(s.key)).map((s) => s.title);

  if (!elevatedAdmin && titles.length === 0) return <span className="td-muted">(없음)</span>;

  const hidden = expanded ? 0 : Math.max(0, titles.length - ACCESS_CHIP_LIMIT);
  const shown = expanded ? titles : titles.slice(0, ACCESS_CHIP_LIMIT);

  return (
    <div className="access-chips">
      {elevatedAdmin && <span className="badge badge--danger">최고 관리자</span>}
      {shown.map((t) => (
        <span className="badge" key={t}>
          {t}
        </span>
      ))}
      {hidden > 0 && (
        <button type="button" className="access-chips__more" onClick={() => setExpanded(true)}>
          +{hidden}개 더
        </button>
      )}
      {expanded && titles.length > ACCESS_CHIP_LIMIT && (
        <button type="button" className="access-chips__more" onClick={() => setExpanded(false)}>
          접기
        </button>
      )}
    </div>
  );
}

function SectionAccessCheckboxes({
  value,
  onChange,
  /** 본인 계정을 편집 중이면, 이 화면에 다시 못 들어오게 되는 조작을 막는다. */
  lockSelfAccess = false,
}: {
  value: AccessFormState;
  onChange: (next: AccessFormState) => void;
  lockSelfAccess?: boolean;
}) {
  function toggleElevated(next: boolean) {
    onChange({ ...value, elevatedAdmin: next });
  }

  function toggleSection(key: string, next: boolean) {
    onChange({
      ...value,
      sections: next ? [...value.sections, key] : value.sections.filter((k) => k !== key),
    });
  }

  const allKeys = SELECTABLE_SECTIONS.map((s) => s.key);
  const allChecked = allKeys.every((k) => value.sections.includes(k));
  // 본인 계정에서 '최고 관리자 권한'을 끄면 이 화면을 다시 못 연다 - 섹션은
  // 어느 것도 이 화면(관리자 계정)을 열어주지 못한다(설정 섹션 자체가 없다).
  // 서버도 같은 조건으로 막지만(auth.service.ts assertNotSelfLockout),
  // 화면에서는 애초에 못 끄게 해서 실수 자체를 없앤다.
  const elevatedIsLastKey = lockSelfAccess && value.elevatedAdmin;

  return (
    <div>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={value.elevatedAdmin}
          disabled={elevatedIsLastKey}
          title={elevatedIsLastKey ? '본인 계정의 마지막 접근 권한이라 끌 수 없습니다.' : undefined}
          onChange={(e) => toggleElevated(e.target.checked)}
        />
        최고 관리자 권한 (은행입금·이메일다운로드·GA/도메인 등록 등 민감 기능 포함)
      </label>
      <p className="panel__note">
        켜면 아래 섹션과 별개로 전체 화면에 접근할 수 있습니다. 다만 '설정'(관리자 계정·화이트 IP)만은
        예외로, xadmin 계정만 볼 수 있습니다 - 이 화면에서 다른 계정에게 개별로 줄 수 없습니다.
      </p>

      <fieldset className="section-access__group">
        <legend>메뉴 섹션 (섹션마다 따로 켜고 끌 수 있습니다)</legend>
        <div className="section-access__bulk">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={allChecked}
            onClick={() => onChange({ ...value, sections: allKeys })}
          >
            전체 선택
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={value.sections.length === 0}
            onClick={() => onChange({ ...value, sections: [] })}
          >
            전체 해제
          </button>
        </div>
        <div className="checkbox-group">
          {SELECTABLE_SECTIONS.map((s) => (
            <label className="checkbox" key={s.key}>
              <input
                type="checkbox"
                checked={value.sections.includes(s.key)}
                onChange={(e) => toggleSection(s.key, e.target.checked)}
              />
              {s.title}
            </label>
          ))}
        </div>
      </fieldset>

      <p className="panel__note">
        {!value.elevatedAdmin && value.sections.length === 0
          ? '최고 관리자 권한 또는 섹션을 하나 이상 선택하세요.'
          : `선택된 섹션 ${value.sections.length}개${value.elevatedAdmin ? ' + 최고 관리자 권한' : ''}`}
      </p>
    </div>
  );
}

interface EditForm {
  authname: string;
  position: string;
  access: AccessFormState;
  isActive: boolean;
  password: string;
}

export function AdminAccountsPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [userid, setUserid] = useState('');
  const [password, setPassword] = useState('');
  const [authname, setAuthname] = useState('');
  const [position, setPosition] = useState('');
  const [access, setAccess] = useState<AccessFormState>({ elevatedAdmin: false, sections: DEFAULT_SECTION_KEYS });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);

  const accounts = useQuery({
    queryKey: ['admins'],
    queryFn: () => adminsApi.list(),
  });

  const create = useMutation({
    mutationFn: () =>
      adminsApi.create({
        userid: userid.trim(),
        password,
        authname: authname.trim(),
        position: position.trim(),
        authtypes: buildAuthtypes(access),
      }),
    onSuccess: () => {
      setUserid('');
      setPassword('');
      setAuthname('');
      setPosition('');
      void qc.invalidateQueries({ queryKey: ['admins'] });
    },
  });

  const update = useMutation({
    mutationFn: (id: number) => {
      const f = editForm!;
      return adminsApi.update(id, {
        authname: f.authname.trim(),
        position: f.position.trim(),
        authtypes: buildAuthtypes(f.access),
        isActive: f.isActive,
        password: f.password.trim() ? f.password.trim() : undefined,
      });
    },
    onSuccess: () => {
      setEditingId(null);
      setEditForm(null);
      void qc.invalidateQueries({ queryKey: ['admins'] });
    },
  });

  const hasAccess = access.elevatedAdmin || access.sections.length > 0;
  const canSubmitCreate = hasAccess && authname.trim() !== '' && position.trim() !== '';

  function submit(e: FormEvent) {
    e.preventDefault();
    if (create.isPending || !canSubmitCreate) return;
    create.mutate();
  }

  function startEdit(a: AdminAccount) {
    update.reset();
    setEditingId(a.id);
    setEditForm({
      authname: a.authname ?? '',
      position: a.position ?? '',
      access: authtypesToFormState(a.authtypes),
      isActive: a.isActive,
      password: '',
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm(null);
  }

  function setEditAccess(next: AccessFormState) {
    setEditForm((prev) => (prev ? { ...prev, access: next } : prev));
  }

  function saveEdit(e: FormEvent, id: number) {
    e.preventDefault();
    if (update.isPending || !editForm) return;
    if (!editForm.access.elevatedAdmin && editForm.access.sections.length === 0) return;
    if (!editForm.authname.trim() || !editForm.position.trim()) return;
    update.mutate(id);
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            관리자 계정 등록
            <span className="badge badge--danger">admin 전용</span>
          </h1>
          <p className="page-desc">
            여기서 등록하는 계정은 레거시 admin_login 과 별개인 신규 테이블에 저장됩니다.
            아이디가 레거시 admin_login 에 이미 있으면 등록이 거부됩니다.
          </p>
        </div>
      </header>

      <div className="panel">
        <form className="form-grid" onSubmit={submit}>
          <Field label="아이디">
            <input
              className="input"
              value={userid}
              onChange={(e) => setUserid(e.target.value)}
              placeholder="영문·숫자·밑줄"
              maxLength={50}
              required
            />
          </Field>
          <Field label={`비밀번호 (${MIN_PASSWORD_LENGTH}자 이상)`}>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={200}
              required
            />
          </Field>
          <Field label="표시 이름">
            <input
              className="input"
              value={authname}
              onChange={(e) => setAuthname(e.target.value)}
              maxLength={50}
              required
            />
          </Field>
          <Field label="직책">
            <input
              className="input"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="팀장, 매니저 등"
              maxLength={50}
              required
            />
          </Field>
          <Field label="접근 가능한 메뉴 섹션" full as="div">
            <SectionAccessCheckboxes value={access} onChange={setAccess} />
          </Field>

          <div className="editor__actions">
            <button className="btn" type="submit" disabled={create.isPending || !canSubmitCreate}>
              {create.isPending ? '등록 중…' : '계정 등록'}
            </button>
          </div>
        </form>

        {create.isError && <ErrorBox error={create.error} />}
        {create.isSuccess && (
          <p className="panel__note">
            <strong>{create.data.account.userid}</strong> 계정을 등록했습니다.
          </p>
        )}
      </div>

      <div className="panel">
        <h2 className="panel__title">등록된 계정</h2>
        {accounts.isPending ? (
          <Spinner />
        ) : accounts.isError ? (
          <ErrorBox error={accounts.error} onRetry={() => void accounts.refetch()} />
        ) : accounts.data.accounts.length === 0 ? (
          <EmptyBox>등록된 계정이 없습니다.</EmptyBox>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>아이디</th>
                  <th>이름</th>
                  <th>등급</th>
                  <th style={{ textAlign: 'center' }}>상태</th>
                  <th>등록자</th>
                  <th>등록일</th>
                  <th style={{ textAlign: 'center' }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {accounts.data.accounts.map((a) =>
                  editingId === a.id && editForm ? (
                    <tr key={a.id}>
                      <td colSpan={7}>
                        <form className="form-grid" onSubmit={(e) => saveEdit(e, a.id)}>
                          <Field label="아이디">
                            <input className="input" value={a.userid} disabled readOnly />
                          </Field>
                          <Field label="새 비밀번호 (선택, 그대로 두면 안 바뀜)">
                            <input
                              className="input"
                              type="password"
                              value={editForm.password}
                              onChange={(e) =>
                                setEditForm((prev) => (prev ? { ...prev, password: e.target.value } : prev))
                              }
                              autoComplete="new-password"
                              minLength={MIN_PASSWORD_LENGTH}
                              maxLength={200}
                              placeholder={`비우면 유지 (바꾸려면 ${MIN_PASSWORD_LENGTH}자 이상)`}
                            />
                          </Field>
                          <Field label="표시 이름">
                            <input
                              className="input"
                              value={editForm.authname}
                              onChange={(e) =>
                                setEditForm((prev) => (prev ? { ...prev, authname: e.target.value } : prev))
                              }
                              maxLength={50}
                              required
                            />
                          </Field>
                          <Field label="직책">
                            <input
                              className="input"
                              value={editForm.position}
                              onChange={(e) =>
                                setEditForm((prev) => (prev ? { ...prev, position: e.target.value } : prev))
                              }
                              placeholder="팀장, 매니저 등"
                              maxLength={50}
                              required
                            />
                          </Field>
                          <Field label="상태" as="div">
                            <label className="checkbox">
                              <input
                                type="checkbox"
                                checked={editForm.isActive}
                                disabled={a.userid === user?.userid}
                                title={
                                  a.userid === user?.userid
                                    ? '본인 계정은 비활성화할 수 없습니다 (로그인이 바로 막힙니다).'
                                    : undefined
                                }
                                onChange={(e) =>
                                  setEditForm((prev) => (prev ? { ...prev, isActive: e.target.checked } : prev))
                                }
                              />
                              사용중 (해제하면 로그인 불가)
                            </label>
                          </Field>
                          <Field label="접근 가능한 메뉴 섹션" full as="div">
                            <SectionAccessCheckboxes
                              value={editForm.access}
                              onChange={setEditAccess}
                              lockSelfAccess={a.userid === user?.userid}
                            />
                          </Field>

                          <div className="editor__actions">
                            <button
                              className="btn"
                              type="submit"
                              disabled={
                                update.isPending ||
                                (!editForm.access.elevatedAdmin && editForm.access.sections.length === 0) ||
                                !editForm.authname.trim() ||
                                !editForm.position.trim()
                              }
                            >
                              {update.isPending ? '저장 중…' : '저장'}
                            </button>
                            <button
                              className="btn btn--ghost"
                              type="button"
                              onClick={cancelEdit}
                              disabled={update.isPending}
                            >
                              취소
                            </button>
                          </div>
                          {update.isError && <ErrorBox error={update.error} />}
                        </form>
                      </td>
                    </tr>
                  ) : (
                    <tr key={a.id}>
                      <td>
                        {a.userid}
                        {a.userid === user?.userid && (
                          <span className="badge badge--info account-self-badge" title="지금 로그인한 계정입니다.">
                            나
                          </span>
                        )}
                      </td>
                      <td className="td-muted">
                        {a.authname ?? '-'}
                        {a.position && ` (${a.position})`}
                      </td>
                      <td>
                        <AccessCell authtypes={a.authtypes} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {a.isActive ? (
                          <span className="badge badge--ok">사용중</span>
                        ) : (
                          <span className="td-muted">비활성</span>
                        )}
                      </td>
                      <td className="td-muted">{a.createdBy ?? '-'}</td>
                      <td className="td-muted">{datetime(a.createdAt)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button className="btn btn--ghost btn--sm" type="button" onClick={() => startEdit(a)}>
                          수정
                        </button>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
